import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  computed,
  output,
  input,
} from '@angular/core';
import { translateSignal } from '@jsverse/transloco';
import { ContentLanguage } from 'src/app/shared/constants/languages';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent implements OnChanges {
  readonly appearance = input<string>(undefined);
  readonly changeValue = input<string>(undefined);
  readonly color = input<string>(undefined);
  readonly extendLangs = input<boolean>(undefined);
  readonly immutable = input(false);
  readonly limitLangs = input<any[]>(undefined);
  readonly left = input<boolean>(undefined);
  readonly longLang = input<boolean>(undefined);
  readonly placeholder = input('');
  readonly model = input<any>(undefined);
  readonly simple = input(false);
  readonly disabled = input<boolean>(undefined);
  readonly showClear = input<boolean>(undefined);
  readonly prefix = input<boolean>(false);

  readonly changed = output<any>();
  readonly clear = output<MouseEvent>();

  emptyModel = {};

  private readonly languagesBase: ContentLanguage[] = [
    'en',
    'it',
    'fr',
    'de',
    'es',
  ];
  private readonly languagesExtendedBase: ContentLanguage[] = [
    'ca',
    'hr',
    'pl',
    'pt',
    'nl',
    'uk',
    'ru',
    'el',
    'da',
    'sv',
    'ar',
    'eu',
    'gl',
    'et',
    'fi',
    'zh',
    'th',
    'vi',
  ];

  private readonly langPrefix = computed(() =>
    this.simple() && !this.longLang() ? 'short_languages' : 'languages',
  );

  private readonly languagesKeys = computed(() =>
    this.languagesBase.map((value) => `shared.${this.langPrefix()}.${value}`),
  );
  private readonly languagesExtendedKeys = computed(() =>
    this.languagesExtendedBase.map(
      (value) => `shared.${this.langPrefix()}.${value}`,
    ),
  );

  private readonly languagesTranslations = translateSignal(this.languagesKeys);
  private readonly languagesExtendedTranslations = translateSignal(
    this.languagesExtendedKeys,
  );

  readonly languages = computed<
    { value: ContentLanguage; viewValue: string }[]
  >(() => {
    const translations = this.languagesTranslations();
    return this.languagesBase.map((value, index) => ({
      value,
      viewValue: translations[index],
    }));
  });

  readonly languagesExtended = computed<
    { value: ContentLanguage; viewValue: string }[]
  >(() => {
    const translations = this.languagesExtendedTranslations();
    return this.languagesExtendedBase.map((value, index) => ({
      value,
      viewValue: translations[index],
    }));
  });

  ngOnChanges() {
    const changeValue = this.changeValue();
    const model = this.model();
    if (changeValue && model) {
      this.emptyModel[changeValue] = model[changeValue];
    }
  }

  handleChange({ value }) {
    if (this.immutable()) {
      this.changed.emit({
        ...this.model(),
        [this.changeValue()]: value,
      });
    } else {
      this.changed.emit({});
    }
  }
}
